/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Stack, styled } from '@mui/material';
import { Dispatch, FC, FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import Typography from '../../../../components/Typography';
import { useDrawdownRequestContext } from './DrawdownRequestContext';
import { TestIds } from '../../../../testIds';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { ModalActions } from '../../../../components/Modal';
import { Button } from '../../../../components/Button';
import { DatePicker } from '../../../../components/DateRangePicker';
import {
  DeliverySaveState,
  PickupSaveState,
  usePickUpStoreComponents,
} from '../../../cart/components/PickUpStoreModal';
import { CartDeliveryMode } from '../../../cart/storage/cart.types';
import { SelectedStoreButton } from '../../../products/components/StockAvailability/StoreSelector';
import { selectStores } from '../../../stores/storage/stores.slice';
import { PadActionButton } from '../../../../components/Pad/Pad';
import { SelectedAddressButton } from '../../v1/forms/AddressSelect';
import Input from '../../../../components/Input';
import { selectConfigByKey } from '../../../../services/firebase/remote-config/remoteConfig.slice';
import { splitAddress } from '../../../../services/als/splitAddress';
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage';

interface DeliveryDetailsButtonProps {
  setShowSelector: Dispatch<SetStateAction<boolean>>;
  hasError?: boolean;
}

const DeliveryDetailsButton: FC<DeliveryDetailsButtonProps> = ({
  setShowSelector,
  hasError,
}: DeliveryDetailsButtonProps) => {
  const isFulfilmentEnabled: boolean = useSelector(selectConfigByKey('drawdownFulfilmentEnabled'));
  const { state } = useDrawdownRequestContext();

  if (state.userEdited?.pickupStoreSelected) {
    return (
      <SelectedStoreButton
        value={state.userEdited.pickupStoreSelected}
        showHours={false}
        onClickEdit={() => setShowSelector(isFulfilmentEnabled)}
        isEditable={isFulfilmentEnabled}
      />
    );
  } else if (state.userEdited?.addressSelected) {
    return (
      <SelectedAddressButton
        customPadding='.75rem'
        onClickEdit={() => setShowSelector(isFulfilmentEnabled)}
        isEditable={isFulfilmentEnabled}
        addressName={state.userEdited.addressSelected.addressName}
        addressShort={state.userEdited.addressSelected.address}
        postCode={state.userEdited.addressSelected.postCode}
      />
    );
  } else {
    return (
      <PadActionButton
        onClick={() => setShowSelector(true)}
        buttonTestId={TestIds.feat.drawdown.v2.step2.deliveryDetailsButton}
        title='Choose pickup or delivery'
        hasError={hasError}
      />
    );
  }
};

const AlertContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  padding: '18px',
  backgroundColor: theme.colors.secondary100,
  alignItems: 'flex-start',
}));

const Step2 = () => {
  const { state, setState, salesAgreement } = useDrawdownRequestContext();
  const account = useSelector(selectCurrentFarmerAccount);
  const [hasAddressNotSelectedError, setHasAddressNotSelectedError] = useState(false);

  const onSavePickup = (_state: PickupSaveState) => {
    setState({
      userEdited: {
        ...state.userEdited,
        pickupStoreSelected: _state.pickupStore,
        addressSelected: undefined,
      },
    });
    setHasAddressNotSelectedError(false);
  };

  const onSaveDelivery = (_state: DeliverySaveState) => {
    setState({
      userEdited: {
        ...state.userEdited,
        addressSelected: _state.delivery.deliveryDetails,
        pickupStoreSelected: undefined,
      },
    });
    setHasAddressNotSelectedError(false);
  };

  const stores = useSelector(selectStores);

  useEffect(() => {
    if (
      account &&
      state.drawdownConfig?.deliveryType === 'Delivery' &&
      salesAgreement?.deliveryAddress &&
      !state.userEdited?.addressSelected
    ) {
      const address = account.addresses.find(
        (e) => e.recordId === salesAgreement.deliveryAddressRecordId,
      );
      if (address) {
        const { first } = splitAddress(address.fullAddress);
        setState({
          userEdited: {
            addressSelected: {
              ...state.userEdited?.addressSelected,
              address: address.fullAddress,
              addressName: address.name,
              streetAddress: first,
              postCode: address.zipCode,
              recordId: address.recordId,

              ...address,
            },
          },
        });
      }
    } else if (
      state.drawdownConfig?.deliveryType === 'Pickup' &&
      state.drawdownConfig?.siteId &&
      !state.userEdited?.pickupStoreSelected &&
      !state.userEdited?.addressSelected
    ) {
      const storeMatch = stores.find((e) => e.inventoryLocationId === state.drawdownConfig?.siteId);
      if (storeMatch) {
        setState({ userEdited: { pickupStoreSelected: storeMatch } });
      }
    }
  }, [state.drawdownConfig, salesAgreement]);

  const [showSelector, setShowSelector] = useState(false);
  const openStoreSelector = (value: boolean) => setShowSelector(value);

  const pickupAllowed = state.drawdownConfig?.deliveryOptions
    .map((option) => option.type)
    .includes('Pickup');
  const deliveryAllowed = state.drawdownConfig?.deliveryOptions
    .map((option) => option.type)
    .includes('Delivery');

  const { footer, container, selectMode } = usePickUpStoreComponents(
    {
      cartDeliveryMode:
        state.drawdownConfig?.deliveryType === 'Delivery'
          ? CartDeliveryMode.DELIVERY
          : CartDeliveryMode.PICKUP,
      pickupStore: state.userEdited?.pickupStoreSelected,
      deliveryDetails: state.userEdited?.addressSelected, // Map deliverInfo details here
      addressSettings: {
        dairyNumberEnabled: false,
        deliveryInstructionsDisabled: true,
        saveAddressEnabled: false,
        urgentEnabled: false,
        dateDisabled: true,
        defaultToSearch: true,
        enableAddAddress: false,
      },
    },

    pickupAllowed ? onSavePickup : undefined,
    deliveryAllowed ? onSaveDelivery : undefined,
    () => openStoreSelector(false),
  );

  if (showSelector) {
    return (
      <>
        {container}
        <ModalActions>{footer}</ModalActions>
      </>
    );
  }

  return (
    <Stack>
      <Typography
        variant='body2'
        fontWeight={'bold'}
        color='primary500'
        data-testid={TestIds.feat.drawdown.v2.step2.title}
      >
        Set {state.userEdited?.addressSelected ? 'delivery' : 'pickup'} details
      </Typography>

      <Stack direction='row' gap={0.5}>
        <Typography
          variant='caption'
          color='grey900'
          data-testid={TestIds.feat.drawdown.v2.step2.subtitle}
        >
          Confirm {state.userEdited?.addressSelected ? 'delivery' : 'pickup'} date and&nbsp;
          {state.userEdited?.addressSelected ? 'delivery' : 'pickup'} location
        </Typography>

        <Typography variant='caption' color='grey600'>
          (Required)
        </Typography>
      </Stack>

      <Stack mt={2}>
        <DeliveryDetailsButton
          setShowSelector={setShowSelector}
          hasError={hasAddressNotSelectedError}
        />
      </Stack>

      {hasAddressNotSelectedError && (
        <Typography
          mt={1}
          variant='caption'
          color='error600'
          data-testid={TestIds.feat.drawdown.v2.step2.addressErrorMessage}
        >
          Please set your pickup or delivery address
        </Typography>
      )}

      {!hasAddressNotSelectedError &&
        state.drawdownConfig?.freightNote &&
        state.drawdownConfig.deliveryType === 'Delivery' && (
          <Typography
            mt={1}
            variant='caption'
            color='grey900'
            data-testid={TestIds.feat.drawdown.v2.step2.freightNote}
          >
            {state.drawdownConfig?.freightNote}
          </Typography>
        )}

      <DeliveryForm
        selectMode={selectMode}
        onNextClick={(isAddressError: boolean | undefined) =>
          setHasAddressNotSelectedError(!!isAddressError)
        }
      />
    </Stack>
  );
};

const DeliveryForm: FunctionComponent<{
  selectMode: CartDeliveryMode;
  onNextClick: (isAddressError?: boolean) => void;
}> = ({ selectMode, onNextClick }) => {
  const { state, setState, salesAgreement } = useDrawdownRequestContext();
  const [isEarlyDelivery, setIsEarlyDelivery] = useState(false);
  const selectedMonth = state.selectedMonth?.value;

  // Calculate minDate and maxDate based on selected month
  const getDateConstraints = () => {
    // find min and max date of all products in the month
    const products = state.productRequest.map((product) => {
      return {
        startDate: dayjs(product.startDate),
        endDate: dayjs(product.endDate),
      };
    });
    const minDate = dayjs(
      Math.min(...products.map((product) => product.startDate.toDate().getTime())),
    );
    const maxDate = dayjs(
      Math.max(...products.map((product) => product.endDate.toDate().getTime())),
    );

    // Get normal constraints
    const today = dayjs();
    const firstDayOfSelectedMonth = dayjs(selectedMonth).startOf('month');

    let min, max;

    // If selected month is current month or previous month
    if (
      firstDayOfSelectedMonth.isSame(today, 'month') ||
      firstDayOfSelectedMonth.isBefore(today, 'month')
    ) {
      min = today.addBusinessDays(1);
      max = today.addBusinessDays(20);
    } else {
      min = firstDayOfSelectedMonth.isBusinessDay()
        ? firstDayOfSelectedMonth
        : firstDayOfSelectedMonth.addBusinessDays(1);

      max = firstDayOfSelectedMonth.addBusinessDays(20);
    }

    // If selected month is future month
    return {
      min: dayjs(
        Math.max(
          min.toDate().getTime(),
          dayjs(salesAgreement?.startDate).toDate().getTime(),
          minDate.toDate().getTime(),
        ),
      ),
      max: dayjs(
        Math.min(
          max.toDate().getTime(),
          dayjs(salesAgreement?.endDate).toDate().getTime(),
          maxDate.toDate().getTime(),
        ),
      ),
    };
  };

  const { min, max } = getDateConstraints();

  const deliveryInitialDate = (() => {
    if (state.userEdited?.preferredDate) {
      return state.userEdited?.preferredDate;
    }

    const leadDays = state.drawdownConfig?.fulfilmentLeadDays ?? 5;

    const today = dayjs();
    const firstDayOfSelectedMonth = dayjs(selectedMonth).startOf('month');

    // If selected month is current month
    if (firstDayOfSelectedMonth.isSame(today, 'month')) {
      return today.addBusinessDays(leadDays);
    }

    // If selected month is next month
    if (firstDayOfSelectedMonth.isAfter(today, 'month')) {
      const businessDaysUntilNextMonth = today.businessDaysDiff(firstDayOfSelectedMonth);

      // If there are less than 5 business days until next month
      if (businessDaysUntilNextMonth < leadDays) {
        return today.addBusinessDays(leadDays);
      }

      // If there are 5 or more business days until next month
      // Get next business day if first day is not a business day
      const nextBusinessDay = firstDayOfSelectedMonth.addBusinessDays(1);
      return firstDayOfSelectedMonth.isBusinessDay() ? firstDayOfSelectedMonth : nextBusinessDay;
    }

    return today.addBusinessDays(leadDays); // fallback
  })();

  const formik = useFormik({
    initialValues: {
      date: deliveryInitialDate,
      deliveryInstructions: state.drawdownConfig?.deliveryInstructions ?? '',
      dairySupplyNumber: state.drawdownConfig?.dairySupplyNumber ?? '',
    },
    onSubmit: (values) => {
      setState({
        userEdited: {
          ...state.userEdited,
          deliveryInstructions: values.deliveryInstructions,
          dairySupplyNumber: values.dairySupplyNumber,
          preferredDate: values.date,
        },
      });

      setState({ tabIndex: 2 });
    },
  });

  const [dateError, setDateError] = useState(false);
  return (
    <Stack mt={3} data-testid={TestIds.feat.drawdown.v2.step2.container}>
      <Stack>
        <Stack direction={'column'} gap={'4px'}>
          <Typography variant='body2' fontWeight='bold' color='grey800'>
            Preferred {selectMode === CartDeliveryMode.DELIVERY ? 'delivery' : 'pickup'} date
          </Typography>

          <DatePicker
            value={formik.values.date}
            setDate={(newValue) => {
              const value = newValue ?? undefined;
              if (
                dayjs(value).isAfter(min.add(-1, 'day')) &&
                dayjs(value).isBefore(max.add(1, 'day')) &&
                dayjs(value).isBusinessDay()
              ) {
                formik.setFieldValue('date', value);
                setDateError(false);
              } else {
                setDateError(true);
              }

              if (
                value &&
                dayjs().endOf('day').businessDaysDiff(value) <
                  (state.drawdownConfig?.fulfilmentLeadDays ?? 0)
              ) {
                setIsEarlyDelivery(true);
              } else setIsEarlyDelivery(false);
            }}
            minDate={min}
            maxDate={max}
            testId={'Todo:delivery_date'}
          />

          {dateError && (
            <Typography variant='caption' color='error700'>
              Please enter a valid date
            </Typography>
          )}

          <Typography variant='caption' color='grey900'>
            Standard {selectMode === CartDeliveryMode.DELIVERY ? 'delivery' : 'pickup'} time
            is&nbsp; {state.drawdownConfig?.fulfilmentLeadDays ?? '3'} working days
          </Typography>
        </Stack>

        {isEarlyDelivery && (
          <AlertContainer gap={1} mt={2}>
            <MaterialSymbol color='#151618'>error</MaterialSymbol>
            {selectMode === CartDeliveryMode.DELIVERY ? (
              <Stack direction={'column'} gap={1}>
                <Typography variant='body2' fontWeight={'bold'} color='grey900'>
                  Your preferred delivery day is less than our standard timeframe
                </Typography>
                <Typography variant='body2' color='grey900'>
                  If we can't fulfil this request you will be contacted by a Farmlands team member
                </Typography>
              </Stack>
            ) : (
              <Stack direction={'column'} gap={1}>
                <Typography variant='body2' fontWeight={'bold'} color='grey900'>
                  Your preferred pickup day is less than our standard timeframe
                </Typography>
                <Typography variant='body2' color='grey900'>
                  We will review your request and be in contact to confirm the delivery timeframe
                </Typography>
              </Stack>
            )}
          </AlertContainer>
        )}

        {state.userEdited?.addressSelected && (
          <>
            <Stack direction={'column'} gap={'4px'} mt={3}>
              <Input
                testId=''
                label='Delivery instructions'
                name='deliveryInstructions'
                type='text'
                onChange={formik.handleChange}
                value={formik.values.deliveryInstructions}
                errorMessage={''}
              />
            </Stack>

            <Stack direction={'column'} gap={'4px'} mt={2}>
              <Input
                testId=''
                label='Dairy supply number'
                name='dairySupplyNumber'
                type={'text'}
                onChange={formik.handleChange}
                value={formik.values.dairySupplyNumber}
                errorMessage={''}
              />
            </Stack>

            {state.drawdownConfig?.freightType && (
              <Stack direction={'column'} gap={'4px'} mt={2}>
                <Typography variant='body2' fontWeight='bold' color='grey800'>
                  Freight option
                </Typography>
                <Typography variant='body2' color='grey800'>
                  {state.drawdownConfig?.freightType}
                </Typography>
              </Stack>
            )}
            {state.drawdownConfig?.carrier && (
              <Stack direction={'column'} gap={'4px'} mt={2}>
                <Typography variant='body2' fontWeight='bold' color='grey800'>
                  Carrier details
                </Typography>
                <Typography variant='body2' color='grey800'>
                  {state.drawdownConfig?.truck} - {state.drawdownConfig?.carrier}
                </Typography>
              </Stack>
            )}
          </>
        )}
        <ModalActions showDivider>
          {dateError && (
            <Typography variant='body2' color='error700' sx={{ mr: 'auto' }}>
              Please enter a valid date
            </Typography>
          )}

          <Button
            onClick={() => {
              onNextClick(
                state.drawdownConfig?.deliveryType === 'Delivery' &&
                  !state.userEdited?.addressSelected,
              );
              (state.drawdownConfig?.deliveryType === 'Pickup' ||
                state.userEdited?.addressSelected) &&
                !dateError &&
                formik.handleSubmit();
            }}
            variant='contained'
            data-testid={TestIds.feat.drawdown.v2.step2.nextButton}
          >
            Next
          </Button>
        </ModalActions>
      </Stack>
    </Stack>
  );
};

export default Step2;
